import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "digirobot_edit_mode";

const EditModeContext = createContext({
  isEditMode: false,
  setEditMode: () => {},
  toggleEditMode: () => {},
  canEdit: false,
});

/** Lit la préférence session : "1" ON, "0" OFF, absence = défaut ON pour admin. */
function readStoredEditMode() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === "0") return false;
    if (raw === "1") return true;
  } catch {
    // ignore
  }
  // Pas de préférence → ON par défaut (admin voit tout de suite les zones éditables)
  return true;
}

export function EditModeProvider({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const canEdit = !loading && isAuthenticated && isAdmin;

  const [isEditMode, setIsEditMode] = useState(readStoredEditMode);

  // Quand l'admin devient connecté sans préférence explicite OFF, force ON
  useEffect(() => {
    if (loading || !canEdit) return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "0") {
        setIsEditMode(false);
        return;
      }
      // absence ou "1" → édition active
      setIsEditMode(true);
      if (sessionStorage.getItem(STORAGE_KEY) !== "1") {
        sessionStorage.setItem(STORAGE_KEY, "1");
      }
    } catch {
      setIsEditMode(true);
    }
  }, [loading, canEdit]);

  // Désactive le mode édition si l'admin se déconnecte
  useEffect(() => {
    if (!loading && !canEdit && isEditMode) {
      setIsEditMode(false);
    }
  }, [loading, canEdit, isEditMode]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("edit-mode-on", canEdit && isEditMode);
    return () => document.body.classList.remove("edit-mode-on");
  }, [canEdit, isEditMode]);

  const setEditMode = useCallback(
    (next) => {
      if (!canEdit) return;
      const value = Boolean(next);
      setIsEditMode(value);
      try {
        // Toujours persister pour distinguer OFF explicite du défaut
        sessionStorage.setItem(STORAGE_KEY, value ? "1" : "0");
      } catch {
        // ignore
      }
    },
    [canEdit]
  );

  const toggleEditMode = useCallback(() => {
    setEditMode(!isEditMode);
  }, [isEditMode, setEditMode]);

  const value = useMemo(
    () => ({
      isEditMode: canEdit && isEditMode,
      setEditMode,
      toggleEditMode,
      canEdit,
    }),
    [canEdit, isEditMode, setEditMode, toggleEditMode]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditMode() {
  return useContext(EditModeContext);
}
